import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import "./HallRentals.css";

// Component to display full details in a modal
const FullFormDetails = ({ rental }) => {
  return (
    <div className="full-form-details">
      {Object.entries(rental)
        .filter(([key]) => key !== "id") // Remove ID
        .map(([key, value]) => {
          let displayValue;
          if (key === "supporting_docs" && value) {
            displayValue = (
              <a href={value} target="_blank" rel="noopener noreferrer">
                View File
              </a>
            );
          } else if (key === "agreed_terms") {
            displayValue = value === "1" || value === 1 ? "Yes" : "No";
          } else {
            displayValue = value || "—";
          }
          return (
            <div key={key} className="detail-row">
              <strong>{key.replace(/_/g, " ")}:</strong>{" "}
              <span>{displayValue}</span>
            </div>
          );
        })}
    </div>
  );
};

const HallRentals = ({ setIsAnyModalActive }) => {
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [modalConfig, setModalConfig] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [activeRental, setActiveRental] = useState(null);
  const [isSelectingText, setIsSelectingText] = useState(false);

  const fetchRentals = () => {
    const tableWrapper = document.querySelector(".table-wrapper");
    const scrollTop = tableWrapper ? tableWrapper.scrollTop : 0;

    fetch("https://admin-new.lasgidirestaurant.com/php/get-hall-rentals.php")
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          const sortedRentals = data.data.sort(
            (a, b) => new Date(a.date_submitted) - new Date(b.date_submitted)
          );
          setRentals(sortedRentals);
        }
        if (tableWrapper) tableWrapper.scrollTop = scrollTop;
      })
      .catch((err) => console.error("Error fetching rentals:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRentals();
    const interval = setInterval(fetchRentals, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleDelete = async (id) => {
    setActionLoading(true);
    try {
      const res = await fetch("https://admin-new.lasgidirestaurant.com/php/delete-hall-rental.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });
      const data = await res.json();
      if (data.success) {
        fetchRentals();
        closeModal();
      } else {
        alert("Delete failed: " + data.message);
      }
    } catch (err) {
      console.error(err);
      alert("Delete error.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!activeRental) return;
    setActionLoading(true);
    try {
      const res = await fetch("https://admin-new.lasgidirestaurant.com/php/update-hall-notes.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: activeRental.id, notes: noteText }),
      });
      const data = await res.json();
      if (data.success) {
        setRentals((prev) =>
          prev.map((r) =>
            r.id === activeRental.id ? { ...r, notes: noteText } : r
          )
        );
      } else {
        alert("Failed to save notes: " + data.message);
      }
    } catch (err) {
      console.error(err);
      alert("Error saving notes.");
    } finally {
      setActionLoading(false);
      closeModal();
    }
  };

  const openConfirmModal = (title, onConfirm, content) => {
    setModalConfig({ title, onConfirm, content, type: "CONFIRM_MODAL" });
    if (setIsAnyModalActive) setIsAnyModalActive(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openNotesModal = (rental) => {
    setActiveRental(rental);
    setNoteText(rental.notes || "");
    setModalConfig({
      title: `Notes for ${rental.full_name}`,
      content: "NOTES_MODAL",
    });
    if (setIsAnyModalActive) setIsAnyModalActive(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openFullDetailsModal = (rental) => {
    setModalConfig({
      content: <FullFormDetails rental={rental} />,
      type: "DETAIL_MODAL",
    });
    if (setIsAnyModalActive) setIsAnyModalActive(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeModal = () => {
    setModalConfig(null);
    setActiveRental(null);
    if (setIsAnyModalActive) setIsAnyModalActive(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="hall-admin-container">
      <h2>Hall Rental Applications</h2>

      {(loading || actionLoading) && (
        <div className="spinner-overlay">
          <div className="spinner"></div>
        </div>
      )}

      {!loading && rentals.length === 0 ? (
        <p className="no-users-found">No records found.</p>
      ) : (
        !loading && (
          <div className="table-wrapper">
            <table className="hall-table">
              <thead>
                <tr>
                  <th>S/N</th>
                  <th>Full Name</th>
                  <th>Contact</th>
                  <th>Email</th>
                  <th>Street Address</th>
                  <th>Country</th>
                  <th>City</th>
                  <th>Postcode</th>
                  <th>Event Type</th>
                  <th>Event Date</th>
                  <th>Start Time</th>
                  <th>End Time</th>
                  <th>Guests</th>
                  <th>Special Requirements</th>
                  <th>Supporting Docs</th>
                  <th>Agreed Terms</th>
                  <th>Signature</th>
                  <th>Date Submitted</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rentals.map((rental, index) => (
                  <tr
                    key={rental.id}
                    onMouseDown={() => setIsSelectingText(false)}
                    onMouseMove={() => setIsSelectingText(true)}
                    onMouseUp={(e) => {
                      if (
                        !isSelectingText &&
                        !["BUTTON", "A", "I", "TEXTAREA"].includes(
                          e.target.tagName
                        )
                      ) {
                        openFullDetailsModal(rental);
                      }
                    }}
                    style={{ cursor: "text" }} // allow selection
                  >
                    <td className="copyable-text">{index + 1}</td>
                    <td className="copyable-text">{rental.full_name}</td>
                    <td className="copyable-text">{rental.contact_number}</td>
                    <td className="copyable-text">{rental.email}</td>
                    <td className="copyable-text">{rental.street_address}</td>
                    <td className="copyable-text">{rental.country}</td>
                    <td className="copyable-text">{rental.city}</td>
                    <td className="copyable-text">{rental.postcode}</td>
                    <td className="copyable-text">{rental.event_type}</td>
                    <td className="copyable-text">
                      {new Date(rental.event_date).toLocaleDateString("en-GB")}
                    </td>
                    <td className="copyable-text">{rental.start_time}</td>
                    <td className="copyable-text">{rental.end_time}</td>
                    <td className="copyable-text">{rental.guests}</td>
                    <td className="copyable-text">
                      {rental.special_requirements || "-"}
                    </td>
                    <td className="copyable-text">
                      {rental.supporting_docs ? (
                        <a
                          href={rental.supporting_docs}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View File
                        </a>
                      ) : (
                        "None"
                      )}
                    </td>
                    <td className="copyable-text">
                      {rental.agreed_terms === "1" || rental.agreed_terms === 1
                        ? "Yes"
                        : "No"}
                    </td>
                    <td className="copyable-text">
                      {rental.signature || "No signature"}
                    </td>
                    <td className="copyable-text">
                      {new Date(rental.date_submitted).toLocaleDateString(
                        "en-GB"
                      )}
                    </td>
                    <td className="copyable-text notes-column">
                      {rental.notes || "—"}
                    </td>
                    <td
                      className="actions-column"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div>
                        <button
                          className="edit-btn"
                          onClick={() => openNotesModal(rental)}
                          title="View or Edit Notes"
                        >
                          <i className="fa fa-sticky-note"></i>
                        </button>
                        <button
                          className="reply-btn"
                          onClick={() =>
                            (window.location.href = `mailto:${rental.email}`)
                          }
                          title="Reply via Email"
                        >
                          <i className="fa fa-envelope"></i>
                        </button>
                        <button
                          className="call-btn"
                          onClick={() =>
                            (window.location.href = `tel:${rental.contact_number}`)
                          }
                          title="Call Customer"
                        >
                          <i className="fa fa-phone"></i>
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() =>
                            openConfirmModal(
                              "Delete Hall Rental",
                              () => handleDelete(rental.id),
                              <div className="delete-modal-heading">
                                <p>
                                  Are you sure you want to delete hall rental for{" "}
                                  <strong>{rental.full_name}</strong>?
                                </p>
                              </div>
                            )
                          }
                          title="Delete Hall Rental"
                        >
                          <i className="fa fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {modalConfig && (
        <Modal
          title={modalConfig.title}
          onClose={closeModal}
          showCancel={modalConfig.type === "CONFIRM_MODAL"}
          showSubmitButton={modalConfig.type === "CONFIRM_MODAL"}
          submitButtonText={actionLoading ? "Deleting..." : "Delete"}
          onSubmit={async (e) => {
            e.preventDefault();
            if (modalConfig.onConfirm) await modalConfig.onConfirm();
          }}
        >
          {modalConfig.content === "NOTES_MODAL" ? (
            <div className="notes-modal-inner">
              <textarea
                className="notes-textarea"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Enter payment notes or any remarks..."
                autoFocus
              />
              <div className="modal-buttons">
                <button
                  type="button"
                  className="profiles-btn cancel-btn"
                  onClick={closeModal}
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="profiles-btn add-btn"
                  onClick={async () => await handleSaveNotes()}
                  disabled={actionLoading}
                >
                  {actionLoading ? "Saving..." : "Save Notes"}
                </button>
              </div>
            </div>
          ) : (
            modalConfig.content
          )}
        </Modal>
      )}
    </div>
  );
};

export default HallRentals;
